package dev.danko.roblox_craft_ai.shared.animation

/**
 * Finger bones of RobloxCraft's template skeleton: three per finger, five fingers per hand, named like
 * "RightIndex1" (base) .. "RightIndex3" (tip), the base parented to the hand. The R15 figure has no fingers, so they
 * only exist on own characters; at rest they continue the hand (pointing down along -Y in the R15 frame).
 *
 * Curl: in the R15 rest pose the palms face the thighs, so a right hand curls its fingers toward -X (Z turn,
 * negative), a left hand toward +X; the thumb folds across the palm toward the front (X turn).
 */
object R15Fingers {
    val FINGERS = listOf("Thumb", "Index", "Middle", "Ring", "Pinky")
    const val SEGMENTS_PER_FINGER = 3

    data class Bone(val name: String, val parent: String, val side: String, val finger: String, val index: Int)

    val BONES: List<Bone> = listOf("Left", "Right").flatMap { side ->
        FINGERS.flatMap { finger ->
            (1..SEGMENTS_PER_FINGER).map { i ->
                Bone("$side$finger$i", if (i == 1) "${side}Hand" else "$side$finger${i - 1}", side, finger, i)
            }
        }
    }
    val NAMES: List<String> = BONES.map { it.name }
    val BY_NAME: Map<String, Bone> = BONES.associateBy { it.name }

    /** Rest direction of every finger bone in the R15 frame (along the hanging hand). */
    val REST_DIRECTION = Vec3(0.0, -1.0, 0.0)

    /** [x, y, z] degrees bending a finger bone by [curl] degrees toward the palm. */
    fun curl(bone: Bone, curl: Double): List<Double> = when {
        bone.finger == "Thumb" -> listOf(-curl * 0.6, 0.0, 0.0)
        bone.side == "Right" -> listOf(0.0, 0.0, -curl)
        else -> listOf(0.0, 0.0, curl)
    }
}
